/**
 * L'encadrement de chantier, lu dans les trois projections de
 * `20261029180000` : `site_team_today`, `site_schedule`, `site_reports`.
 *
 * Aucune n'expose plus qu'un nom : le chef de chantier ne lit pas la fiche des
 * salariés. La base décide du chantier — un appel hors de ses chantiers rend
 * des listes vides, pas une erreur.
 */
import { DAY_PARTS, DomainError } from "@dker/core";
import type {
  DayPart,
  ExecutionContext,
  FieldReportKind,
  FieldReportSeverity,
  FieldReportStatus,
  SiteLeadRepository,
  SiteReport,
  SiteScheduleEntry,
  SiteTeamMember,
} from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Lecture refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

const dayPart = (value: unknown): DayPart =>
  (DAY_PARTS as readonly unknown[]).includes(value) ? (value as DayPart) : "journee";

export function createSupabaseSiteLeadRepository(clientFor: DbClientFactory): SiteLeadRepository {
  return {
    async team(ctx: ExecutionContext, projectId: string): Promise<SiteTeamMember[]> {
      const { data, error } = await clientFor(ctx).rpc("site_team_today", { p_project_id: projectId });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map((r) => ({
        employeeId: r.employee_id,
        name: r.employee_name,
        roleOnSite: r.role_on_site ?? null,
        dayPart: dayPart(r.day_part),
        arrivedAt: r.arrived_at ?? null,
        leftAt: r.left_at ?? null,
      }));
    },

    async schedule(ctx: ExecutionContext, projectId: string, days: number): Promise<SiteScheduleEntry[]> {
      const { data, error } = await clientFor(ctx).rpc("site_schedule", {
        p_project_id: projectId,
        p_days: days,
      });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map((r) => ({
        date: r.work_date,
        employeeId: r.employee_id,
        name: r.employee_name,
        roleOnSite: r.role_on_site ?? null,
        dayPart: dayPart(r.day_part),
      }));
    },

    async reports(ctx: ExecutionContext, projectId: string): Promise<SiteReport[]> {
      const { data, error } = await clientFor(ctx).rpc("site_reports", { p_project_id: projectId });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map((r) => ({
        id: r.id,
        createdAt: r.created_at,
        kind: r.kind as FieldReportKind,
        severity: r.severity as FieldReportSeverity,
        status: r.status as FieldReportStatus,
        message: r.message,
        authorName: r.author_name ?? null,
        phaseName: r.phase_name ?? null,
        declaredProgressPercent: r.declared_progress_percent ?? null,
      }));
    },
  };
}
